/**
 * ScanRuleRow: one rule's mode, field, match type, and value. The value is
 * a combobox over observed values for exact matches and a free pattern
 * input for regex.
 */

import { X } from 'lucide-react'

import { RowAction } from '@/components/settings/RowAction'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

import { RuleValueCombobox } from './RuleValueCombobox'
import {
  FIELD_LABELS,
  FIELD_OPTIONS,
  MATCH_LABELS,
  MATCH_OPTIONS,
  MODE_LABELS,
  MODE_OPTIONS,
  type ScanRule,
} from './scanRules'

interface ScanRuleRowProps {
  index: number
  rule: ScanRule
  suggestions: readonly string[]
  onChange: (rule: ScanRule) => void
  onRemove: () => void
}

export function ScanRuleRow({
  index,
  rule,
  suggestions,
  onChange,
  onRemove,
}: ScanRuleRowProps): React.JSX.Element {
  const n = String(index + 1)
  return (
    <li className="flex flex-wrap items-center gap-2">
      <span className="w-4 shrink-0 text-right font-mono text-[11px] text-muted-foreground" aria-hidden="true">
        {n}
      </span>
      <RuleSelect
        ariaLabel={`Rule ${n} mode`}
        className="w-32"
        value={rule.mode}
        options={MODE_OPTIONS}
        labels={MODE_LABELS}
        onChange={(mode) => {
          onChange({ ...rule, mode })
        }}
      />
      <RuleSelect
        ariaLabel={`Rule ${n} field`}
        className="w-44"
        value={rule.field}
        options={FIELD_OPTIONS}
        labels={FIELD_LABELS}
        onChange={(field) => {
          onChange({ ...rule, field })
        }}
      />
      <RuleSelect
        ariaLabel={`Rule ${n} match type`}
        className="w-36"
        value={rule.match}
        options={MATCH_OPTIONS}
        labels={MATCH_LABELS}
        onChange={(match) => {
          onChange({ ...rule, match })
        }}
      />
      <span className="min-w-40 flex-1">
        {rule.match === 'exact' ? (
          <RuleValueCombobox
            value={rule.value}
            suggestions={suggestions}
            onChange={(value) => {
              onChange({ ...rule, value })
            }}
            ariaLabel={`Rule ${n} value`}
          />
        ) : (
          <Input
            className="font-mono"
            value={rule.value}
            onChange={(e) => {
              onChange({ ...rule, value: e.target.value })
            }}
            placeholder="pattern, e.g. ^[A-Za-z0-9]{20}$"
            aria-label={`Rule ${n} value`}
          />
        )}
      </span>
      <RowAction label={`Remove rule ${n}`} icon={X} onClick={onRemove} />
    </li>
  )
}

function RuleSelect<T extends string>({
  ariaLabel,
  className,
  value,
  options,
  labels,
  onChange,
}: {
  ariaLabel: string
  className: string
  value: T
  options: readonly T[]
  labels: Record<T, string>
  onChange: (value: T) => void
}): React.JSX.Element {
  return (
    // Radix only emits values from the items below, so the cast is exact.
    <Select
      value={value}
      onValueChange={(next) => {
        onChange(next as T)
      }}
    >
      <SelectTrigger className={cn(className, 'shrink-0')} aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {labels[option]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
